import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../Pages/CreateEditPage.css";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import FileUploadComponent from "./FileUploadComponent.tsx";
import { Cantieri, Contratti, Task } from "../lib/interface.ts";
import ModalFormTask from "./ModalFormTask.tsx";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Redux/Store/index.ts";
import { allGet, allPatch, allPost, allPut } from "../Redux/Actions.tsx";
import { urlcantiere, urlcontratti, urlfiles, urltask } from "../lib/api.ts";
import { useAlert } from "./Alert.tsx";
import DeleteModal from "./DeleteModal.tsx";
import { Dropdown } from "primereact/dropdown";
import { format } from "date-fns";
import { InputNumber } from "primereact/inputnumber";
import DatePicker, { registerLocale } from "react-datepicker";
import { it } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("it", it);

interface FormCantieriProps {
  action: "crea" | "modifica";
  type: any;
  id?: any;
}

const FormCantieri: React.FC<FormCantieriProps> = ({ action, type, id }) => {
  const [cantiere, setCantiere] = useState<Cantieri>({
    id: "",
    nome: "",
    indirizzo: "",
    citta: "",
    cap: "",
    dataInizio: "",
    dataFinePrevista: "",
    dataFineEffettiva: "",
    importo: 0,
    subappalto: "false",
    subappaltoAzienda: "",
    subappaltoImporto: 0,
    territorio: "",
    stato: "",
    utenteAssegnatoId: "",
    utentePrepostoId: "",
    contrattoQuadro: "",
    note: "",
    committenteId: "",
    referenti: [],
    eventi: [],
    tasks: [],
  });

  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.login);
  const { task } = useSelector((state: RootState) => state.task);
  const { stati } = useSelector((state: RootState) => state.stati);
  const committenti = useSelector((state: RootState) => state.committenti.committenti || []);
  const utenti = useSelector((state: RootState) => state.utenti.utenti || []);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contrattiCommittente, setContrattiCommittente] = useState<Contratti[]>([]);
  const [isEditing, setIsEditing] = useState<boolean>(!id);

  const showAlert = useAlert();
  const contratti = useSelector((state: RootState) => state.contratti.contratti || []);
  const dateToString = (date: Date) => format(date, "yyyy-MM-dd");
  const [searchTextCommittente, setSearchTextCommittente] = useState("");
  const [searchTextContratto, setSearchTextContratto] = useState("");
  const [searchTextUtenteAssegnato, setSearchTextUtenteAssegnato] = useState("");
  const [searchTextUtentePreposto, setSearchTextUtentePreposto] = useState("");
  const [searchTextStato, setSearchTextStato] = useState("");
  const [errors, setErrors] = useState({
    nome: false,
  });

  const validateFields = () => {
    const newErrors = {
      nome: !cantiere.nome.trim(),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  const [principalFileName, setPrincipalFileName] = useState<string | null>(null);
  const [principalFileUrl, setPrincipalFileUrl] = useState<string | null>(null);

  const fetchPrincipalFile = async (contrattoId: string) => {
    try {
      // Recupera il contratto
      const contrattoUrl = `${urlcontratti}/${contrattoId}`;
      const contratto = await dispatch(allGet(contrattoUrl, token));

      if (contratto && contratto.documentoPrincipale) {
        // Recupera i dettagli dei file associati al contratto
        const fileUrl = `${urlfiles}/folder?folderKey=contratti/${contratto.codice}`;
        const files = await dispatch(allGet(fileUrl, token));
        const principalFile = files.find((file: any) => file.id === contratto.documentoPrincipale);

        if (principalFile) {
          // Genera il presigned URL per il file principale
          const presignedUrlResponse = await fetch(`${urlfiles}/presigned-url?filePath=${principalFile.fileKey}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (presignedUrlResponse.ok) {
            const presignedUrl = await presignedUrlResponse.text();
            setPrincipalFileName(principalFile.fileKey.split("/").pop());
            setPrincipalFileUrl(presignedUrl); // Salva l'URL
          } else {
            console.error("Errore nel recupero del presigned URL");
            setPrincipalFileName(principalFile.fileKey.split("/").pop());
            setPrincipalFileUrl(null);
          }
        } else {
          setPrincipalFileName(null);
          setPrincipalFileUrl(null);
        }
      } else {
        setPrincipalFileName(null);
        setPrincipalFileUrl(null);
      }
    } catch (error) {
      console.error("Errore nel recupero del file principale:", error);
      setPrincipalFileName(null);
      setPrincipalFileUrl(null);
    }
  };

  useEffect(() => {
    if (cantiere.contrattoQuadro) {
      fetchPrincipalFile(cantiere.contrattoQuadro);
    } else {
      setPrincipalFileName(null);
      setPrincipalFileUrl(null);
    }
  }, [cantiere.contrattoQuadro]);

  useEffect(() => {
    if (action === "modifica" && id) {
      const url = `${urlcantiere}/${id}`;
      dispatch(allGet(url, token)).then((singoloCantiere) => {
        if (singoloCantiere) {
          setCantiere(singoloCantiere);
          const tasks = task.filter((t) => t.cantiereId === id);
          setCantiere((prev) => ({
            ...prev,
            tasks: tasks,
          }));
        } else {
          console.error("Errore nel caricamento del cantiere");
        }
      });
    }
  }, [action, id]);

  useEffect(() => {
    if (action === "modifica" && cantiere.contrattoQuadro) {
      const contrattoAssociato = contratti.find((contratto) => contratto.id === cantiere.contrattoQuadro);

      if (contrattoAssociato) {
        // Imposta committenteId e contrattiCommittente per la modifica
        setCantiere((prev) => ({
          ...prev,
          committenteId: contrattoAssociato.committenteId,
        }));

        setContrattiCommittente(
          contratti.filter((contratto) => contratto.committenteId === contrattoAssociato.committenteId)
        );
      }
    }
  }, [action, cantiere.contrattoQuadro, contratti]);

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleChange = (field: keyof Cantieri, value: any) => {
    setCantiere((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Comportamento specifico per Committente in modalità creazione
    if (field === "committenteId" && action === "crea") {
      setContrattiCommittente(contratti.filter((contratto) => contratto.committenteId === value));
    }

    // Rimuove eventuali errori per il campo modificato
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: false,
    }));
  };

  const handleDateChange = (field: keyof Cantieri, value: Date | Date[] | null | undefined) => {
    setCantiere((prev) => ({
      ...prev,
      [field]: value ? dateToString(value as Date) : "",
    }));
  };

  const handleAddTask = (newTask: Task) => {
    setCantiere((prev) => ({
      ...prev,
      tasks: [...prev.tasks, newTask],
    }));
  };

  const handleClose = () => {
    navigate("/cantieri");
  };

  const handleSubmit = () => {
    if (!validateFields()) {
      console.error("Errore: campi obbligatori non compilati.");
      showAlert("danger", "Errore", "Compila tutti i campi obbligatori.", "bottom-end", 3000);
      return;
    }
    if (action === "crea") {
      dispatch(allPost(urlcantiere, token, cantiere, showAlert)).then((id) => {
        if (id) {
          showAlert("success", "Creato", "Cantiere creato con successo.", "bottom-end", 3000);

          cantiere.tasks.forEach((task) => {
            const updatedTask = {
              ...task,
              cantiereId: id,
            };
            dispatch(allPost(urltask, token, updatedTask, showAlert)).then(() => {
              console.log("Task aggiunto:", updatedTask);
            });
          });
        } else {
          console.error("Creazione del cantiere fallita");
        }
      });
    } else if (action === "modifica" && id) {
      const url = `${urlcantiere}/${id}`;
      dispatch(allPut(url, token, cantiere)).then((updatedCantiere) => {
        if (updatedCantiere) {
          console.log("Cantiere aggiornato:", updatedCantiere);
          showAlert("success", "Salvato", "Cantiere aggiornato con successo.", "bottom-end", 3000);
          setIsEditing(false);
        } else {
          console.error("Modifica del cantiere fallita");
        }
      });
    }
    handleClose();
  };
  const updateTask = (updatedTask: Task) => {
    setCantiere((prevCantiere) => ({
      ...prevCantiere,
      tasks: prevCantiere.tasks.map((task) => (task.id === updatedTask.id ? updatedTask : task)),
    }));
  };
  const setArrayTask = (arrayTask: Task[]) => {
    setCantiere((prevCantiere) => ({
      ...prevCantiere,
      tasks: arrayTask,
    }));
  };

  return (
    <div className="home-container">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-center mb-5">
        <h2 className="titolo-header-Crea-Edit">
          Cantiere/<b>{action === "crea" ? "Nuovo Cantiere" : cantiere.nome}</b>
        </h2>
        <div className="d-flex">
          {isEditing ? (
            <Button className="ms-2 button d-flex px-3" onClick={handleSubmit}>
              <i className="pi pi-save me-2"></i>
              <p className="p-0 m-0">Salva</p>
            </Button>
          ) : (
            <Button className="ms-2 button d-flex px-3" onClick={handleEdit}>
              <i className="pi pi-pencil me-2"></i>
              <p className="p-0 m-0">Modifica</p>
            </Button>
          )}
          {action === "modifica" && (
            <Button onClick={() => setIsDeleteModalOpen(true)} className="ms-2 button d-flex px-3">
              <i className="pi pi-trash me-2"></i>
              <p className="p-0 m-0">Elimina</p>
            </Button>
          )}
          <Button className="ms-2 button d-flex px-3" onClick={handleClose}>
            <i className="pi pi-times me-2"></i>
            <p className="p-0 m-0">Chiudi</p>
          </Button>
        </div>
      </div>
      <Row className="g-3">
        <Col xs={12} className="px-0">
          <div className="create-edit-top">
            <Row className="row-form">
              {/* Left Column */}
              <Col lg={4} className="colonna-form">
                {/* Nome */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Nome *</Form.Label>
                  <InputText
                    className="w-50 input-crea-edit"
                    value={cantiere.nome}
                    onChange={(e) => handleChange("nome", e.target.value)}
                    placeholder="Inserisci nome"
                    disabled={!isEditing}
                    style={errors.nome ? { border: "1px solid red" } : {}}
                  />
                </Form.Group>
                {/* Indirizzo */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Indirizzo</Form.Label>
                  <InputText
                    className="w-50 input-crea-edit"
                    name="indirizzo"
                    value={cantiere.indirizzo}
                    onChange={(e) => handleChange("indirizzo", e.target.value)}
                    placeholder="Inserisci indirizzo"
                    disabled={!isEditing}
                  />
                </Form.Group>
                {/* Città */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Città</Form.Label>
                  <InputText
                    className="w-50 input-crea-edit"
                    name="citta"
                    value={cantiere.citta}
                    onChange={(e) => handleChange("citta", e.target.value)}
                    placeholder="Inserisci città"
                    disabled={!isEditing}
                  />
                </Form.Group>
                {/* CAP */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>CAP</Form.Label>
                  <InputText
                    className="w-50 input-crea-edit"
                    name="cap"
                    value={cantiere.cap}
                    onChange={(e) => handleChange("cap", e.target.value)}
                    placeholder="Inserisci CAP"
                    disabled={!isEditing}
                  />
                </Form.Group>
                {/* Stato */}

                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Stato *</Form.Label>
                  <Dropdown
                    className="w-50 input-crea-edit p-0"
                    value={cantiere.stato}
                    options={stati
                      .map((stato) => ({
                        label: stato.stato.charAt(0).toUpperCase() + stato.stato.slice(1).replace("_", " "),
                        value: stato.id,
                      }))
                      .filter((option) => option.label.toLowerCase().includes(searchTextStato.toLowerCase()))}
                    onChange={(e) => handleChange("stato", e.value)}
                    onInput={(e) => setSearchTextStato((e.target as HTMLInputElement).value)}
                    placeholder="Seleziona Stato"
                    editable
                    disabled={!isEditing}
                    emptyMessage="Nessuna opzione disponibile"
                  />
                </Form.Group>
                {/* Territorio */}
                <Form.Group className="px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Territorio</Form.Label>
                  <InputText
                    className="w-50 input-crea-edit"
                    name="territorio"
                    value={cantiere.territorio}
                    onChange={(e) => handleChange("territorio", e.target.value)}
                    placeholder="Inserisci territorio"
                    disabled={!isEditing}
                  />
                </Form.Group>
              </Col>

              {/* Center Column */}
              <Col lg={4} className="colonna-centrale colonna-form">
                {/* Committente */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Committente</Form.Label>
                  <Dropdown
                    className="w-50 input-crea-edit p-0"
                    value={cantiere.committenteId}
                    options={committenti.map((committente) => ({
                      label: committente.ragioneSociale,
                      value: committente.id,
                    }))}
                    onChange={(e) => handleChange("committenteId", e.value)}
                    placeholder="Seleziona Committente"
                    editable
                    disabled={action === "modifica"} // Solo modifica lo disabilita
                  />
                </Form.Group>

                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Contratto Quadro *</Form.Label>
                  <Dropdown
                    className="w-50 input-crea-edit p-0"
                    value={cantiere.contrattoQuadro}
                    options={contrattiCommittente.map((contratto) => ({
                      label: contratto.codice,
                      value: contratto.id,
                    }))}
                    onChange={(e) => handleChange("contrattoQuadro", e.value)}
                    placeholder="Seleziona Contratto Quadro"
                    editable
                    disabled={action === "modifica"} // Solo modifica lo disabilita
                    emptyMessage={
                      !cantiere.committenteId ? "Seleziona prima un committente" : "Nessuna opzione disponibile"
                    }
                  />
                </Form.Group>

                {/* Documento contratto */}
                {action === "modifica" && (
                  <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                    <Form.Label>Documento del contratto</Form.Label>
                    {principalFileName ? (
                      <Button
                        className="w-50 button"
                        onClick={() => {
                          if (principalFileUrl) {
                            window.open(principalFileUrl, "_blank");
                          }
                        }}
                      >
                        {principalFileName}
                      </Button>
                    ) : (
                      <>
                        <InputText disabled className="w-50 input-crea-edit" value={"Nessun documento principale"} />
                      </>
                    )}
                  </Form.Group>
                )}

                {/* Importo */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Importo</Form.Label>

                  <InputNumber
                    disabled={!isEditing}
                    className="w-50 input-crea-edit"
                    inputId="currency-germany"
                    value={cantiere.importo === 0 ? null : cantiere.importo}
                    onChange={(e) =>
                      setCantiere((prev) => ({
                        ...prev,
                        importo: parseFloat(e.value?.toString() || "0"),
                      }))
                    }
                    mode="currency"
                    currency="EUR"
                    locale="it-IT"
                    placeholder="0,00€"
                  />
                </Form.Group>

                {/* Data Inizio */}
                <Form.Group className="mb-3 px-4 responsive-px  d-flex justify-content-between align-items-center">
                  <Form.Label>Data Inizio</Form.Label>
                  <span className="w-50">
                    <DatePicker
                      className="w-100 input-crea-edit cursor-pointer"
                      locale="it"
                      selected={cantiere.dataInizio ? new Date(cantiere.dataInizio) : null}
                      onChange={(date) => handleDateChange("dataInizio", date)}
                      placeholderText="Seleziona data inizio"
                      dateFormat="dd/MM/yyyy"
                      disabled={!isEditing}
                    />
                  </span>
                </Form.Group>

                {/* Data Fine Prevista */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Data Fine Prevista</Form.Label>
                  <span className="w-50">
                    <DatePicker
                      className="w-100 input-crea-edit cursor-pointer"
                      locale="it"
                      selected={cantiere.dataFinePrevista ? new Date(cantiere.dataFinePrevista) : null}
                      onChange={(date) => handleDateChange("dataFinePrevista", date)}
                      placeholderText="Seleziona data fine prevista"
                      dateFormat="dd/MM/yyyy"
                      disabled={!isEditing}
                      minDate={cantiere.dataInizio ? new Date(cantiere.dataInizio) : undefined}
                    />
                  </span>
                </Form.Group>
                {/* Data Fine Effettiva */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Data Fine Effettiva</Form.Label>
                  <span className="w-50">
                    <DatePicker
                      className="w-100 input-crea-edit cursor-pointer"
                      locale="it"
                      selected={cantiere.dataFineEffettiva ? new Date(cantiere.dataFineEffettiva) : null}
                      onChange={(date) => handleDateChange("dataFineEffettiva", date)}
                      placeholderText="Seleziona data fine effettiva"
                      dateFormat="dd/MM/yyyy"
                      disabled={!isEditing}
                      minDate={cantiere.dataInizio ? new Date(cantiere.dataInizio) : undefined}
                    />
                  </span>
                </Form.Group>
              </Col>

              {/* Right Column */}
              <Col lg={4} className="colonna-form">
                {/* Utente Assegnato */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Utente Assegnato</Form.Label>
                  <Dropdown
                    className="w-50 input-crea-edit p-0"
                    value={cantiere.utenteAssegnatoId}
                    options={utenti
                      .filter((utente) =>
                        `${utente.nome} ${utente.cognome}`
                          .toLowerCase()
                          .includes(searchTextUtenteAssegnato.toLowerCase())
                      )
                      .map((utente) => ({
                        label: `${utente.nome} ${utente.cognome}`,
                        value: utente.id,
                      }))}
                    onChange={(e) => handleChange("utenteAssegnatoId", e.value)}
                    onInput={(e) => setSearchTextUtenteAssegnato((e.target as HTMLInputElement).value)}
                    placeholder="Seleziona Utente Assegnato"
                    editable
                    disabled={!isEditing}
                  />
                </Form.Group>

                {/* Utente Preposto */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Utente Preposto</Form.Label>
                  <Dropdown
                    className="w-50 input-crea-edit p-0"
                    value={cantiere.utentePrepostoId}
                    options={utenti
                      .filter((utente) =>
                        `${utente.nome} ${utente.cognome}`
                          .toLowerCase()
                          .includes(searchTextUtentePreposto.toLowerCase())
                      )
                      .map((utente) => ({
                        label: `${utente.nome} ${utente.cognome}`,
                        value: utente.id,
                      }))}
                    onChange={(e) => handleChange("utentePrepostoId", e.value)}
                    onInput={(e) => setSearchTextUtentePreposto((e.target as HTMLInputElement).value)}
                    placeholder="Seleziona Utente Preposto"
                    editable
                    disabled={!isEditing}
                  />
                </Form.Group>

                {/* Subappalto */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                  <Form.Label>Subappalto</Form.Label>
                  <Form.Check
                    type="switch"
                    id="subappalto-switch"
                    label=""
                    checked={cantiere.subappalto === "true"}
                    onChange={(e) => handleChange("subappalto", e.target.checked ? "true" : "false")}
                    style={{ transform: "scale(1.2)" }}
                    disabled={!isEditing}
                  />
                </Form.Group>

                {cantiere.subappalto === "true" && (
                  <>
                    {/* Subappalto Azienda */}
                    <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                      <Form.Label>Subappalto Azienda</Form.Label>
                      <InputText
                        className="w-50 input-crea-edit"
                        name="subappaltoAzienda"
                        value={cantiere.subappaltoAzienda}
                        onChange={(e) => handleChange("subappaltoAzienda", e.target.value)}
                        placeholder="Inserisci nome azienda"
                        disabled={!isEditing}
                      />
                    </Form.Group>

                    {/* Subappalto Importo */}
                    <Form.Group className="mb-3 px-4 responsive-px d-flex justify-content-between align-items-center">
                      <Form.Label>Subappalto Importo</Form.Label>
                      <InputNumber
                        disabled={!isEditing}
                        className="w-50 input-crea-edit"
                        inputId="currency-germany"
                        value={cantiere.subappaltoImporto === 0 ? null : cantiere.subappaltoImporto}
                        onChange={(e) =>
                          setCantiere((prev) => ({
                            ...prev,
                            subappaltoImporto: parseFloat(e.value?.toString() || "0"),
                          }))
                        }
                        mode="currency"
                        currency="EUR"
                        locale="it-IT"
                        placeholder="0,00€"
                      />
                    </Form.Group>
                  </>
                )}
                {/* Note */}
                <Form.Group className="mb-3 px-4 responsive-px d-flex flex-column h-45">
                  <InputTextarea
                    className="input-crea-edit w-100"
                    name="note"
                    value={cantiere.note}
                    onChange={(e) => handleChange("note", e.target.value)}
                    rows={1}
                    cols={20}
                    placeholder="Inserisci note"
                    disabled={!isEditing}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Col>

        {/* File Upload Section */}
        <Col lg={5} className="ps-0">
          <Container className="create-edit-under">
            {action === "modifica" ? (
              <FileUploadComponent folderType="cantieri" folderKey={cantiere.nome} />
            ) : (
              <>
                <h5 className="text-start m-0">File</h5>
                <br></br>
                <p>Per caricare un file, devi prima aver creato il cantiere</p>
              </>
            )}
          </Container>
        </Col>

        <Col lg={7} className="pe-0">
          <div className="create-edit-under div-flex-bet">
            <ModalFormTask
              arrayTasks={cantiere.tasks}
              onAddTask={handleAddTask}
              updateTask={updateTask}
              setArrayTask={setArrayTask}
              action={action}
              nome={cantiere.nome}
              idPrincipale={id || ""}
              type="cantieri"
            />
          </div>
        </Col>
      </Row>
      <DeleteModal
        type="cantiere"
        idObj={cantiere.id}
        matchIdentifier={cantiere.nome}
        show={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onDeleteSuccess={() => {
          handleClose();
        }}
      />
    </div>
  );
};

export default FormCantieri;
